import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import api, { adminApi } from '@/api'
import { ElNotification } from 'element-plus'
import { useSubscriptionStore, SubscriptionData, SubscriptionStatus } from './subscription'
import { useLanguageStore } from './language'

const safeJSONParse = (str: string | null): any => {
  if (!str) return null;
  try {
    return JSON.parse(str);
  } catch (e) {
    console.error('JSON parse hatası:', e);
    return null;
  }
}

export const useAuthStore = defineStore('auth', () => {
  const router = useRouter()
  const subscriptionStore = useSubscriptionStore()
  const languageStore = useLanguageStore()
  const token = ref(localStorage.getItem('token') || sessionStorage.getItem('token') || '')
  const user = ref(safeJSONParse(localStorage.getItem('user') || sessionStorage.getItem('user')))
  
  // Abonelik bilgisini Admin-API'den al (güvenli - bypass edilemez)
  const fetchSubscriptionFromAdminApi = async (companyId: number) => {
    try {
      const response = await adminApi.get(`/subscriptions/company/${companyId}`)
      return response?.data || null
    } catch (error) {
      console.error('Admin API subscription fetch error:', error)
      return null
    }
  }

  const checkAutoLogin = async () => {
    const storedToken = localStorage.getItem('token') || sessionStorage.getItem('token')
    const storedUser = localStorage.getItem('user') || sessionStorage.getItem('user')
    let isActive = false
    if (storedToken && storedUser) {
      token.value = storedToken
      try {
        user.value = JSON.parse(storedUser)

        if (user.value.is_kiosk_user) {
          subscriptionStore.setSubscriptionStatus(true)
          subscriptionStore.setShowExpirationModal(false)
          return true
        }

        const subscription = await fetchSubscriptionFromAdminApi(user.value.id)
        
        if (subscription && subscription.id !== 0) {
          const endDateOk = subscription.end_date ? new Date(subscription.end_date) > new Date() : false
          const hasGrace = subscription.has_unpaid_order_with_future_date === true
          isActive = (subscription.status === SubscriptionStatus.ACTIVE && endDateOk) || hasGrace

          subscriptionStore.setSubscriptionStatus(isActive)
          subscriptionStore.setSubscriptionData(subscription)
          subscriptionStore.checkAndSetExpirationModal()
        } else {
          subscriptionStore.setSubscriptionStatus(false)
          subscriptionStore.setSubscriptionData({} as SubscriptionData)
          subscriptionStore.setShowExpirationModal(true)
        }
        
        return true
      } catch (e) {
        console.error('Kullanıcı verisi parse edilemedi:', e)
        ElNotification.error(languageStore.t('loginFailed', 'Giriş başarısız!'))
        return false
      }
    }
    return false
  }

  const login = async (email: string, password: string, rememberMe: boolean = false) => {
    try {      
      const response = await api.post(`/auth/login`, { email, password })

      if (response.data && response.data.access_token) {
        token.value = response.data.access_token
        
        user.value = response.data.user || {}

        if (rememberMe) {
          localStorage.setItem('token', token.value)
          localStorage.setItem('user', JSON.stringify(user.value))
        } else {
          sessionStorage.setItem('token', token.value)
          sessionStorage.setItem('user', JSON.stringify(user.value))
        }        
        ElNotification.success(languageStore.t('loginSuccess', 'Giriş başarılı!'))

        if (user.value.is_kiosk_user) {
          subscriptionStore.setSubscriptionStatus(true)
          subscriptionStore.setShowExpirationModal(false)
          router.push('/kiosk')
        } else {
          const subscription = await fetchSubscriptionFromAdminApi(user.value.id)
          if (subscription && subscription.id !== 0) {
            const endDateOk = subscription.end_date ? new Date(subscription.end_date) > new Date() : false
            const hasGrace = subscription.has_unpaid_order_with_future_date === true
            const isActive = (subscription.status === SubscriptionStatus.ACTIVE && endDateOk) || hasGrace

            subscriptionStore.setSubscriptionStatus(isActive)
            subscriptionStore.setSubscriptionData(subscription)

            const hasUnpaidOrders = (subscription.unpaid_orders_count || 0) > 0
            const isExpired = subscription.status !== 'active'
            if (isExpired || hasUnpaidOrders) {
              subscriptionStore.checkAndSetExpirationModal()
            }
            router.push('/')
          } else {
            subscriptionStore.setSubscriptionStatus(false)
            subscriptionStore.setSubscriptionData({} as SubscriptionData)
            subscriptionStore.setShowExpirationModal(true)
            router.push('/')
          }
        }
        return true
      } else {
        let errorMessage = languageStore.t('auth.loginFailed', 'Giriş başarısız!')
        
        if (response.data.message) {
          const backendMessage = response.data.message
          // error_codes.xxx formatındaysa çevir
          if (typeof backendMessage === 'string' && backendMessage.startsWith('error_codes.')) {
            errorMessage = languageStore.t(backendMessage, backendMessage)
          } else if (Array.isArray(backendMessage) && backendMessage.length > 0) {
            const firstMessage = backendMessage[0]
            if (typeof firstMessage === 'string' && firstMessage.startsWith('error_codes.')) {
              errorMessage = languageStore.t(firstMessage, firstMessage)
            } else {
              errorMessage = `${errorMessage}: ${firstMessage}`
            }
          } else {
            errorMessage = `${errorMessage}: ${backendMessage}`
          }
        }
        
        ElNotification.error(errorMessage)
        return false
      }
    } catch (error: any) {
      console.error('Login error:', error)
      
      // Error code çevirisi - backend'den gelen error_codes.xxx formatını çevir
      let errorMessage = languageStore.t('auth.loginFailed', 'Giriş başarısız!')
      
      if (error?.response?.data?.message) {
        const backendMessage = error.response.data.message
        // error_codes.xxx formatındaysa çevir
        if (typeof backendMessage === 'string' && backendMessage.startsWith('error_codes.')) {
          errorMessage = languageStore.t(backendMessage, backendMessage)
        } else if (Array.isArray(backendMessage) && backendMessage.length > 0) {
          // Array formatında gelirse ilk elemanı al
          const firstMessage = backendMessage[0]
          if (typeof firstMessage === 'string' && firstMessage.startsWith('error_codes.')) {
            errorMessage = languageStore.t(firstMessage, firstMessage)
          } else {
            errorMessage = `${errorMessage}: ${firstMessage}`
          }
        } else {
          errorMessage = `${errorMessage}: ${backendMessage}`
        }
      }
      
      ElNotification.error(errorMessage)
      return false
    }
  }

  const logout = () => {
    token.value = ''
    user.value = null
    subscriptionStore.setSubscriptionStatus(false)
    subscriptionStore.setSubscriptionData({} as SubscriptionData)
    localStorage.removeItem('token')
    localStorage.removeItem('user')
    sessionStorage.removeItem('token')
    sessionStorage.removeItem('user')
    sessionStorage.removeItem('permissions')
    router.push('/login')
  }

  const isAuthenticated = () => {
    return !!token.value && token.value !== '' && token.value !== null && user.value !== null && user.value !== undefined && user.value !== ''
  }

  const isSubuser = () => {
    return isAuthenticated() && user.value && user.value.is_subuser && user.value.is_subuser == true
  }

  const sendForgotPasswordEmail = async (email: string) => {
    const response = await adminApi.post('/auth/forgot-password', { email })
    return response.data
  }

  const resetPassword = async (resetToken: string, password: string) => {
    const response = await adminApi.post('/auth/reset-password', { token: resetToken, password })
    return response.data
  }

  const updateUserProfile = (profileData: any) => {
    if (user.value) {
      Object.assign(user.value, profileData)
      
      // LocalStorage ve sessionStorage'ı güncelle
      const storage = localStorage.getItem('token') ? localStorage : sessionStorage
      storage.setItem('user', JSON.stringify(user.value))
    }
  }

  return {
    token,
    user,
    login,
    logout,
    isAuthenticated,
    isSubuser,
    checkAutoLogin,
    sendForgotPasswordEmail,
    resetPassword,
    updateUserProfile,
    fetchSubscriptionFromAdminApi
  }
}) 